/**
 * 报名办理入口 `/enroll/new`
 * -----------------------------------------------------------------------------
 * 类型与批次**由服务端下发**（`GET /api/h5/enroll-options`，2026-10-18 拍板）：
 *   - URL 可选携带 `?enrollTypeId=&batchId=`（兼容入口链接直达，id / code 均可）；
 *     带参时按参匹配列表并显示中文名
 *   - 无参时取列表：唯一组合自动选中；多组合展示选择；空列表给指引
 *   - 确认页展示类型 / 批次中文名（同样来自列表，不再出现「已选择报名类型」占位）
 * 服务端下发口径与创建拦截同源（ACTIVE 类型 × 开放窗口内的批次），
 * 名额满员在创建时由 `ENROLL-001 / ENROLL-002` 兜底。
 */
import { Toast } from 'antd-mobile';
import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { newIdempotencyKey } from '../api/client';
import { describeApiError } from '../api/errors';
import { createRegistration } from '../api/h5';
import { useEnrollOptions } from '../api/hooks';
import type { EnrollOptionView } from '../api/types';
import { CheckIcon } from '../components/icons';
import { Button, Card, EmptyHint, KV, LoadingBlock, Notice, PageShell } from '../components/layout';
import { rememberRegistration } from '../store/recent';

function matchesOption(option: EnrollOptionView, typeRef: string, batchRef: string): boolean {
  const typeHit = option.enrollTypeId === typeRef || option.enrollTypeCode === typeRef;
  const batchHit = option.batchId === batchRef || option.batchCode === batchRef;
  return typeHit && batchHit;
}

export default function EnrollNew() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const paramType = searchParams.get('enrollTypeId');
  const paramBatch = searchParams.get('batchId');
  const options = useEnrollOptions();
  const [busy, setBusy] = useState(false);
  /** 多组合时用户点选的组合 key（`typeId|batchId`） */
  const [pickedKey, setPickedKey] = useState<string | null>(null);

  const items = options.data?.items ?? [];

  /** 入口链接指定的组合（带参才尝试匹配；匹配不到按无参处理走列表选择） */
  const linked = useMemo(() => {
    if (!paramType || !paramBatch) return null;
    return items.find((option) => matchesOption(option, paramType, paramBatch)) ?? null;
  }, [items, paramType, paramBatch]);

  const picked =
    pickedKey === null
      ? null
      : (items.find((option) => `${option.enrollTypeId}|${option.batchId}` === pickedKey) ?? null);
  /** 最终选中：链接指定 > 用户点选 > 列表首项（默认选中，与设计稿一致，少一次点击） */
  const selected = linked ?? picked ?? items[0] ?? null;
  /** 是否需要让学员挑：多组合、且不是入口链接直接点名的那一个 */
  const showPicker = items.length > 1 && !linked;

  const start = async (option: EnrollOptionView) => {
    setBusy(true);
    try {
      // 幂等令牌固定一次：重复点击不会建出两张单（服务端同组合已有非终态单时也直接返回该单）
      const detail = await createRegistration(
        { enrollTypeId: option.enrollTypeId, batchId: option.batchId },
        newIdempotencyKey(),
      );
      rememberRegistration(detail.id);
      navigate(`/enroll/${detail.id}`, { replace: true });
    } catch (error) {
      Toast.show({ icon: 'fail', content: describeApiError(error) });
      setBusy(false);
    }
  };

  return (
    <PageShell
      title="报名办理"
      back
      footer={
        selected ? (
          <Button variant="primary" block loading={busy} onClick={() => void start(selected)}>
            开始填写报名信息
          </Button>
        ) : null
      }
    >
      {options.isPending ? <LoadingBlock text="正在获取可报批次" /> : null}

      {!options.isPending && items.length === 0 ? (
        <>
          <Notice tone="warning">当前没有可报的报名类型与批次，请稍后再试或联系客服确认。</Notice>
          <EmptyHint
            title="暂无可报批次"
            description="批次开放由后台配置，开放后入口会自动出现在这里"
            actionText="返回服务台"
            onAction={() => navigate('/home')}
          />
        </>
      ) : null}

      {!options.isPending && items.length > 0 ? (
        <>
          {/* 一张卡说清「报哪个」：
              多组合 → 直接列出来让学员选（选中的那一行本身就是确认）；
              单组合 / 深链直达 → 卡片只做只读陈述，标题用「本次报名」而不是「确认报名信息」
              —— 这里没有任何需要「确认」的动作，页脚那颗按钮才是唯一的推进动作。 */}
          {selected ? (
            showPicker ? (
              <Card title="选择报名类型与批次" tight>
                {items.map((option) => {
                  const key = `${option.enrollTypeId}|${option.batchId}`;
                  const active =
                    selected.enrollTypeId === option.enrollTypeId &&
                    selected.batchId === option.batchId;
                  return (
                    <button
                      key={key}
                      type="button"
                      className={`option-row option-row--dense${active ? ' option-row--active' : ''}`}
                      onClick={() => setPickedKey(key)}
                    >
                      <span className="option-row__body">
                        <span className="option-row__title">{option.enrollTypeName}</span>
                        <span className="option-row__desc">{option.batchName}</span>
                      </span>
                      <span className={`option-row__mark${active ? ' option-row__mark--on' : ''}`}>
                        {active ? <CheckIcon size={11} /> : null}
                      </span>
                    </button>
                  );
                })}
              </Card>
            ) : (
              <Card title="本次报名" tight>
                <KV label="报名类型" value={selected.enrollTypeName} />
                <KV label="报名批次" value={selected.batchName} />
              </Card>
            )
          ) : null}

          <Notice tone="info">
            提交前需要完成身份核验（证件识别 + 人脸比对 + 证件照），核验未通过将无法提交。
          </Notice>
        </>
      ) : null}
    </PageShell>
  );
}
